import { useState, type FC, type ReactNode } from 'react';
import { Menu, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { DashboardSection } from '../../types/dashboard.types';
import { DashboardSidebar } from '../../components/Dashboard/DashboardSidebar';
import { DashboardHeader } from '../../components/Dashboard/DashboardHeader';
import { SkipLink } from '../../components/accessibility';
import { useAuth } from '../../contexts/AuthContext';

export interface DashboardLayoutProps {
  activeSection: DashboardSection;
  onSectionChange: (section: DashboardSection) => void;
  children: ReactNode;
}

export const DashboardLayout: FC<DashboardLayoutProps> = ({
  activeSection,
  onSectionChange,
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  const handleSectionChange = (section: DashboardSection) => {
    onSectionChange(section);
    setSidebarOpen(false);
  };

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Skip link for keyboard navigation */}
      <SkipLink targetId="dashboard-main-content" label="Skip to dashboard content" />

      {/* Mobile Sidebar Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={cn(
          'lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-md dark:shadow-slate-900/50',
          'hover:shadow-lg transition-shadow focus:outline-none focus:ring-2',
          'focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900'
        )}
        aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={sidebarOpen}
        aria-controls="dashboard-sidebar"
      >
        {sidebarOpen ? (
          <X className="w-6 h-6 text-gray-700 dark:text-slate-300" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700 dark:text-slate-300" />
        )}
      </button>

      {/* Sidebar */}
      <DashboardSidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />

      {/* Main Content Area */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {/* Header */}
        <DashboardHeader
          user={user ? { name: user.name, email: user.email } : undefined}
          onNotificationsClick={() => handleSectionChange('notifications')}
        />

        {/* Page Content */}
        <main id="dashboard-main-content" className="flex-1 p-4 md:p-6 lg:p-8" tabIndex={-1}>
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default DashboardLayout;
